// src/pages/Admin/AdminLogin.js
import { useEffect, useMemo, useState } from "react";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  onIdTokenChanged,
  signOut,
} from "firebase/auth";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { auth, getIsAdmin } from "../firebaseConfig"; // keep your singleton

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();

  // compute next path correctly (pathname + search preserved)
  const next = useMemo(() => {
    const n = sp.get("next");
    if (n && typeof n === "string") return n;
    return "/admin/applications";
  }, [sp]);

  // Optional email whitelist
  const whitelist = useMemo(() => {
    return String(process.env.REACT_APP_ADMIN_WHITELIST || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }, []);

  function inWhitelist(mail) {
    if (!whitelist.length) return true; // if no list, allow by claims only
    return !!mail && whitelist.includes(String(mail).toLowerCase());
  }

  // --- Helpers to sync local session and to logout everywhere ---

  // Persist local session for AdminGate fallback (no top-level import needed)
  async function persistLocalSessionFromFirebaseUser(user, ttlMinutes = 240) {
    try {
      const token = await user.getIdToken(); // short-lived token is fine for our local gate
      const { setSession } = await import("../services/adminAuth");
      await setSession({
        token,
        user: {
          email: user.email || "",
          role: "admin",
          name: user.displayName || "Admin User",
        },
        ttlMinutes,
      });
    } catch {
      // ignore — local session is only a convenience fallback
    }
  }

  // Firebase signOut + clear local auth (and purge firebase:* keys)
  async function hardLogoutEverywhere() {
    try {
      const { adminLogout } = await import("../services/adminAuth");
      await adminLogout(); // firebase signOut + local clear + purge firebase:* keys
    } catch {
      // fallback: try firebase signOut only
      try { await signOut(auth); } catch {}
    }
  }

  // If already logged-in AND admin, auto redirect (also sync local session)
  useEffect(() => {
    const off = onIdTokenChanged(auth, async (u) => {
      if (!u) return;
      try {
        // Prefer your helper
        let isAdmin = false;
        if (typeof getIsAdmin === "function") {
          isAdmin = await getIsAdmin();
        } else {
          const t = await u.getIdTokenResult();
          const claims = t?.claims || {};
          isAdmin =
            claims.admin === true ||
            String(claims.role || "").toLowerCase() === "admin";
        }
        if (isAdmin && inWhitelist(u.email)) {
          try { await persistLocalSessionFromFirebaseUser(u, 240); } catch {}
          navigate(next, { replace: true });
        }
      } catch {
        // ignore — user can still log in manually
      }
    });
    return () => off && off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function humanizeFirebaseError(code, message) {
    if (!code) return message || "Login failed";
    const c = String(code).toLowerCase();
    if (c.includes("invalid-credential")) return "Incorrect email or password.";
    if (c.includes("user-not-found")) return "No admin account found with this email.";
    if (c.includes("wrong-password")) return "Incorrect password.";
    if (c.includes("too-many-requests")) return "Too many attempts. Please try again later.";
    if (c.includes("network-request-failed")) return "Network error. Check your connection.";
    return message || "Login failed";
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      // Persistence first (remember me)
      await setPersistence(
        auth,
        remember ? browserLocalPersistence : browserSessionPersistence
      );

      // sign in
      await signInWithEmailAndPassword(auth, email.trim(), password);

      // check admin claims OR helper OR whitelist
      let isAdmin = false;
      try {
        if (typeof getIsAdmin === "function") {
          isAdmin = await getIsAdmin();
        } else {
          const u = auth.currentUser;
          const t = await u.getIdTokenResult(true); // refresh
          const claims = t?.claims || {};
          isAdmin =
            claims.admin === true ||
            String(claims.role || "").toLowerCase() === "admin";
          // must satisfy whitelist too if defined
          isAdmin = isAdmin && inWhitelist(u?.email);
        }
      } catch {
        isAdmin = false;
      }

      if (!isAdmin) {
        setErr("You are not authorized as Admin.");
        // Important: logout everywhere (firebase + local session)
        await hardLogoutEverywhere();
        return;
      }

      // Admin verified → persist local session (remember? local=240m, session=60m)
      try {
        const u = auth.currentUser;
        await persistLocalSessionFromFirebaseUser(u, remember ? 240 : 60);
      } catch { /* ignore */ }

      navigate(next, { replace: true });
    } catch (e2) {
      const code = e2?.code || "";
      const msg = humanizeFirebaseError(code, e2?.message);
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form
        onSubmit={handleLogin}
        className="bg-white p-8 rounded-2xl shadow-md w-full max-w-md border border-gray-200"
      >
        <h2 className="text-2xl font-extrabold text-center text-green-800 mb-2">
          🔐 Admin Login
        </h2>
        <p className="text-center text-sm text-gray-500 mb-6">
          Use your admin email &amp; password to continue.
        </p>

        {err ? (
          <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
            {err}
          </div>
        ) : null}

        <label className="block font-semibold mb-2 text-sm">Email</label>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full p-2.5 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />

        <div className="flex items-center justify-between">
          <label className="block font-semibold mb-2 text-sm">Password</label>
          <button
            type="button"
            onClick={() => setShowPass((s) => !s)}
            className="text-xs text-green-700 hover:underline"
          >
            {showPass ? "Hide" : "Show"}
          </button>
        </div>
        <input
          type={showPass ? "text" : "password"}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full p-2.5 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />

        <label className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="rounded"
          />
          Remember me on this device
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-700 disabled:bg-green-700/60 text-white py-2.5 rounded-lg hover:bg-green-800 transition-all font-semibold"
        >
          {loading ? "Signing in…" : "🚀 Login"}
        </button>

        <div className="text-xs text-gray-500 mt-3 text-center">
          Access is restricted to admin accounts only.
        </div>
      </form>
    </div>
  );
}

export default AdminLogin;
